import { Title } from '@solidjs/meta'
import { For } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import { componentsIndex } from './_layout'

export default function ComponentIndexPage() {
  return (
    <>
      <Title>Componentes - Florez Jaramillo UI</Title>

      {/* Dynamic Background Mesh */}
      <div class="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div class="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-primary-500/5 blur-[120px] animate-pulse"></div>
        <div class="absolute top-[20%] -right-[10%] w-[35%] h-[35%] rounded-full bg-secondary-500/5 blur-[100px] animate-pulse" style="animation-delay: 2s"></div>
      </div>

      <Container class="py-16 space-y-24">
        {/* Luxury Hero */}
        <section class="relative text-center max-w-4xl mx-auto space-y-8">
          <Badge variant="soft" color="primary" size="sm" class="px-4 py-1 animate-fade-in shadow-sm border border-primary/25 uppercase">
            {__APP_VERSION__}
          </Badge>
          <h1 class="text-5xl md:text-7xl font-black tracking-tight text-foreground leading-[1.1]">
            Arquitectura <span class="text-primary">Visual</span> Superior
          </h1>
          <p class="text-xl md:text-2xl text-foreground-subtle font-medium leading-relaxed max-w-3xl mx-auto">
            Una librería de componentes SolidJS diseñada para ingenieros que no aceptan menos que la perfección estética y el rendimiento bruto.
          </p>
          <div class="flex flex-wrap justify-center gap-4 pt-4">
            <Button variant="contained" size="lg" class="h-14 px-10 rounded-2xl shadow-xl shadow-primary-500/20 active:scale-95 transition-all">
              Explorar Librería
            </Button>
            <Button variant="outlined" size="lg" class="h-14 px-10 rounded-2xl backdrop-blur-sm border-border hover:bg-surface-hover transition-all">
              GitHub Repo
            </Button>
          </div>
        </section>

        {/* Core Pillars */}
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card class="group bg-surface/50 backdrop-blur-xl border-border hover:border-primary-500/50 transition-colors rounded-[2.5rem] overflow-hidden p-2">
            <Card.Body class="p-8 space-y-6">
              <div class="w-16 h-16 rounded-3xl bg-primary-soft flex items-center justify-center text-primary-500 group-hover:scale-110 transition-transform">
                <Icon icon="material-symbols-light:bolt-outline" size={32} />
              </div>
              <div class="space-y-2">
                <h3 class="text-2xl font-bold">Rendimiento Solid</h3>
                <p class="text-foreground-subtle leading-relaxed italic">Reactividad granular sin Virtual DOM. El framework más rápido del ecosistema moderno.</p>
              </div>
            </Card.Body>
          </Card>

          <Card class="group bg-surface/50 backdrop-blur-xl border-border hover:border-secondary-500/50 transition-colors rounded-[2.5rem] overflow-hidden p-2">
            <Card.Body class="p-8 space-y-6">
              <div class="w-16 h-16 rounded-3xl bg-surface-muted flex items-center justify-center text-secondary-500 group-hover:scale-110 transition-transform">
                <Icon icon="material-symbols-light:accessibility" size={32} />
              </div>
              <div class="space-y-2">
                <h3 class="text-2xl font-bold">Accesibilidad AA</h3>
                <p class="text-foreground-subtle leading-relaxed italic">Semántica HTML5 nativa y soporte ARIA completo para experiencias inclusivas de alto nivel.</p>
              </div>
            </Card.Body>
          </Card>

          <Card class="group bg-surface/50 backdrop-blur-xl border-border hover:border-emerald-500/50 transition-colors rounded-[2.5rem] overflow-hidden p-2">
            <Card.Body class="p-8 space-y-6">
              <div class="w-16 h-16 rounded-3xl bg-success-soft flex items-center justify-center text-emerald-500 group-hover:scale-110 transition-transform">
                <Icon icon="material-symbols-light:code" size={32} />
              </div>
              <div class="space-y-2">
                <h3 class="text-2xl font-bold">Type-Safe UX</h3>
                <p class="text-foreground-subtle leading-relaxed italic">Desarrollado íntegramente en TypeScript. Contratos de datos robustos para toda la UI.</p>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Directory Grid */}
        <section class="space-y-12">
          <div class="flex flex-col md:flex-row items-end justify-between gap-4 border-b border-border pb-8 px-4">
            <div class="space-y-2">
              <h2 class="text-4xl font-bold">Catálogo Maestro</h2>
              <p class="text-foreground-subtle">Total: {componentsIndex.length} componentes refinados</p>
            </div>
            <div class="flex items-center gap-2 px-6 py-2 bg-surface-muted rounded-2xl text-xs font-bold text-foreground-subtle uppercase tracking-widest">
              <Icon icon="material-symbols-light:filter-list" size={16} />
              Filtrado por: Todos
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <For each={componentsIndex}>
              {comp => (
                <a
                  href={`/${comp.route}`}
                  class="group relative flex flex-col items-center justify-center p-6 bg-surface rounded-[2rem] border border-border hover:border-primary-500/30 hover:shadow-2xl hover:shadow-primary-500/10 transition-all active:scale-95 overflow-hidden"
                >
                  <div class="absolute inset-x-0 bottom-0 h-1 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                  <div class="w-12 h-12 rounded-2xl bg-surface-muted flex items-center justify-center mb-4 group-hover:bg-primary-soft group-hover:text-primary-500 transition-colors">
                    <Icon icon={comp.icon} size={24} />
                  </div>
                  <span class="text-sm font-bold text-foreground-muted group-hover:text-primary-500 transition-colors">{comp.name}</span>
                </a>
              )}
            </For>
          </div>
        </section>

        {/* Installation CTA */}
        <section class="p-1 max-w-5xl mx-auto">
          <div class="bg-slate-900 rounded-[3rem] p-12 relative overflow-hidden text-center space-y-8">
            <div class="absolute top-0 right-0 w-64 h-64 bg-primary-500/20 blur-[100px] -z-0"></div>
            <h2 class="text-3xl md:text-4xl font-bold text-white relative z-10">Instalación Médula</h2>
            <p class="text-foreground-subtle text-lg max-w-2xl mx-auto relative z-10 italic">Sin configuraciones engorrosas. Solo tecnología pura lista para ser consumida.</p>
            <div class="mx-auto max-w-md bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/10 relative z-10">
              <div class="flex items-center justify-between gap-4 font-mono text-emerald-400 text-sm overflow-x-auto whitespace-nowrap scrollbar-hide">
                <code>bun add @florezjaramillo/florezjaramillo-ui</code>
                <button
                  class="shrink-0 w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  onClick={() => navigator.clipboard.writeText('bun add @florezjaramillo/florezjaramillo-ui')}
                >
                  <Icon icon="material-symbols-light:content-copy-outline" size={20} />
                </button>
              </div>
            </div>
          </div>
        </section>
      </Container>
    </>
  )
}
